// Prizes and rewards (docs/ORGANISER_PLAN.md §5 "Arcade and rewards"; mockups o03, o08).
// The parent keeps a prize pool; children win prizes from the capsule machine; real-world prizes
// wait for a grown-up's OK and never expire. Plays are earned by triggers (daily goal, a check-in,
// an activity a grown-up confirms).
import { z } from 'zod'
import { AgeBand } from './family'

const id = z.string().regex(/^[a-z0-9][a-z0-9-]*$/, 'kebab-case id')

/**
 * Reward machines (B16): the cabinet a child spends a play on and how the reward opens. Only the
 * look and the words change; plays, odds and prizes are the same for every machine. `gashapon`
 * is Japan's capsule machine; `chest` is the neutral one (a placeholder look for now).
 */
export const RewardMachineId = z.enum(['gashapon', 'chest', 'claw'])
export type RewardMachineId = z.infer<typeof RewardMachineId>
/** A trip with no kit, or a kit that names no machine, gets this one. */
export const NEUTRAL_MACHINE: RewardMachineId = 'chest'

export const Rarity = z.enum(['common', 'rare', 'legendary'])
export type Rarity = z.infer<typeof Rarity>

export const Prize = z.object({
  title: z.string().trim().min(1).max(40),
  emoji: z.string().min(1).max(8),
  /** sticker and toy pay out at once; real needs a grown-up's OK. */
  kind: z.enum(['sticker', 'toy', 'real']),
  rarity: Rarity,
  /** Everyone, an age band, or named children. */
  who: z.union([z.literal('all'), AgeBand, z.array(z.string()).min(1)]),
  /** How many times it can be won this trip (real prizes); none = no limit. */
  cap: z.number().int().min(1).max(99).optional(),
  paused: z.boolean().optional(),
  /** WI-14: the parent's note the child sees on a tap ("up to ¥500, at the next dinner"). */
  note: z.string().trim().max(80).optional(),
})
export type Prize = z.infer<typeof Prize>

export const PrizeSet = z.object({ prizeId: id, fields: Prize.partial() })
export const PrizeRemoved = z.object({ prizeId: id })
export const PrizeWon = z.object({ wonId: id, prizeId: id, for: z.string().min(1), sessionId: z.string().optional() })
export const PrizeRedeemed = z.object({ wonId: id, decision: z.enum(['ok', 'swap', 'later']), note: z.string().max(80).optional() })
/** A grown-up confirms a plan with an activity award was done: the plays go to `for`. */
export const RewardAwarded = z.object({ for: z.string().min(1), trigger: z.literal('activity'), itemId: id, plays: z.number().int().min(1).max(5), day: z.string() })
/**
 * A child's phone worked out that a rule paid (the daily goal was met, a check-in was near).
 * The sender is the child; `ref` makes it count once (the day, or the place checked in at).
 */
export const RewardEarned = z.object({
  trigger: z.enum(['daily', 'checkin']),
  ref: z.string().min(1).max(64),
  plays: z.number().int().min(1).max(3),
  day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
})
export type RewardEarned = z.infer<typeof RewardEarned>

/** How often a twist pays a real prize from the parent's list (WI-13 §5 D2: the parent's one choice). */
export const PrizeLevel = z.enum(['off', 'rarely', 'sometimes', 'often'])
export type PrizeLevel = z.infer<typeof PrizeLevel>
/** The chance per twist for each level (WI-13; "sometimes" was the old fixed 1 in 4). */
export const PRIZE_LEVEL_CHANCE: Record<PrizeLevel, number> = { off: 0, rarely: 0.1, sometimes: 0.2, often: 0.35 }

/** How much of the key band opens before the trip (More › "how much opens before the trip"). */
export const PreTrip = z.enum(['little', 'some', 'most'])
export type PreTrip = z.infer<typeof PreTrip>
/** D2 decided 40 % ("some", the default). */
export const PRE_TRIP_SHARE: Record<PreTrip, number> = { little: 0.25, some: 0.4, most: 0.6 }

/** How plays are earned (family-wide; per-child caps live in the child's controls). */
export const RewardRules = z.object({
  daily: z.object({ on: z.boolean(), plays: z.number().int().min(1).max(3) }),
  checkin: z.object({ on: z.boolean(), plays: z.number().int().min(1).max(3), radiusM: z.number().int().min(50).max(2000) }),
  activity: z.object({ on: z.boolean() }),
  /** Plays every child gets each play-day just for opening the Arcade; they don't roll over. */
  free: z.object({ plays: z.number().int().min(0).max(3) }).optional(),
  /** Holiday tasks (WI-13b) pay when ticked; off: the task list is hidden and nothing pays. */
  tasks: z.object({ on: z.boolean() }).optional(),
  /** WI-13: how often a twist pays a real prize; how much of the world opens before the trip. */
  prizes: z.object({ level: PrizeLevel }).optional(),
  release: z.object({ before: PreTrip }).optional(),
})
export type RewardRules = z.infer<typeof RewardRules>
export const RewardRulesSet = z.object({ fields: RewardRules.partial() })

// Provisional (run 2026-10-11, owner to confirm): one free play a day keeps the Arcade open
// before any play is earned. WI-13 (run 2026-10-23): a twist pays a real prize "sometimes".
export const DEFAULT_RULES: RewardRules = {
  daily: { on: true, plays: 1 },
  checkin: { on: true, plays: 1, radiusM: 300 },
  activity: { on: true },
  free: { plays: 1 },
  tasks: { on: true },
  prizes: { level: 'sometimes' },
  release: { before: 'some' },
}

/** Chance that a finished capsule holds a prize from the pool (before WI-13; drawPrize's default). */
export const PRIZE_CHANCE = 0.25
/** When a capsule holds a prize, its rarity (BUILD_PLAN_NEXT §4.2: 70 / 25 / 5). */
export const PRIZE_ODDS: Record<Rarity, number> = { common: 70, rare: 25, legendary: 5 }

/**
 * The starter pool (B8, sample set from the owner 2026-10-14): destination-neutral, so any trip
 * can start from it; the parent adds trip-flavoured prizes in Prizes. Ids whose meaning carried
 * over were kept, because live events may name them.
 */
export const STARTER_PRIZES: Record<string, Prize> = {
  'choose-dinner': { title: 'Choose dinner', emoji: '🍜', kind: 'real', rarity: 'rare', who: 'all', cap: 3 },
  'pick-afternoon': { title: "Pick tomorrow's afternoon", emoji: '🎢', kind: 'real', rarity: 'legendary', who: 'all', cap: 1 },
  'local-snack': { title: 'A local snack of your choice', emoji: '🍦', kind: 'real', rarity: 'common', who: 'all', cap: 8 },
  'spending-money': { title: 'Spending money for a treat', emoji: '🪙', kind: 'real', rarity: 'common', who: '13+', cap: 5 },
  'late-bedtime': { title: '30 min later bedtime', emoji: '🛏', kind: 'real', rarity: 'rare', who: '8-12', paused: true },
  'window-seat': { title: 'Window seat on the next journey', emoji: '🪟', kind: 'real', rarity: 'common', who: 'all', cap: 3 },
  'music-pick': { title: 'Pick the music for a journey', emoji: '🎧', kind: 'real', rarity: 'common', who: 'all' },
  'souvenir': { title: 'Choose a souvenir', emoji: '🎁', kind: 'real', rarity: 'rare', who: 'all', cap: 2 },
  'trip-photographer': { title: 'Trip photographer for a day', emoji: '📷', kind: 'real', rarity: 'rare', who: '13+', cap: 1 },
  'skip-a-job': { title: 'Skip one packing-up job', emoji: '🧳', kind: 'real', rarity: 'common', who: 'all', cap: 3 },
}

/**
 * Starter prizes that were replaced (B8). They're no longer in the pool, but a live event may
 * still name them: a `prize.set` edit on one builds on its old fields (so it becomes a custom
 * prize), and a prize already won still shows its title.
 */
export const RETIRED_PRIZES: Record<string, Prize> = {
  'yen-500': { title: '¥500 to spend', emoji: '💴', kind: 'real', rarity: 'common', who: '13+' },
  'soft-serve': { title: 'Hokkaido soft-serve', emoji: '🍦', kind: 'real', rarity: 'common', who: 'u8', cap: 8 },
}

// ---------- WI-13: what a twist pays, and holiday tasks ----------

/**
 * What one twist paid, decided once on the phone (domain/release `drawPayout`) and recorded by the
 * child: a collectible for their shelf, an act key (the act then runs on that child's own island:
 * a world per child), or a real prize (also recorded as `prize.won`, so Needs you works as before).
 * `bonus`: a key paid for finishing a collectible set, outside the release curve.
 */
export const RewardPaid = z.object({
  twistId: z.string().min(1).max(40),
  for: z.string().min(1),
  day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  kind: z.enum(['collectible', 'key', 'prize']),
  /** collectible: its id (`<place>:<name>`). */
  item: z.string().max(64).optional(),
  /** key: the island and the act it opens. */
  place: z.string().max(40).optional(),
  act: z.string().max(64).optional(),
  bonus: z.boolean().optional(),
  /** prize: the prize and the `prize.won` it made. */
  prizeId: id.optional(),
  wonId: id.optional(),
})
export type RewardPaid = z.infer<typeof RewardPaid>

/** A holiday task (WI-13b): before or during the trip, ticked by the child or confirmed by a grown-up. */
export const Task = z.object({
  title: z.string().trim().min(1).max(60),
  emoji: z.string().min(1).max(8),
  /** Twists it pays. */
  plays: z.number().int().min(1).max(3),
  /** The child ticks it themselves (small things, trust-based); else a grown-up confirms it in Needs you. */
  self: z.boolean(),
  when: z.enum(['before', 'during', 'any']),
  who: z.union([z.literal('all'), AgeBand, z.array(z.string()).min(1)]),
  paused: z.boolean().optional(),
})
export type Task = z.infer<typeof Task>
/**
 * Starter holiday tasks (WI-13b, run 2026-10-23 default): destination-neutral, on until the parent
 * changes them, like the starter prizes. Small ones are self-ticked (under-8s may tick small tasks
 * themselves, D2); bigger ones a grown-up confirms. A region kit can suggest more (kit `tasks`).
 */
export const STARTER_TASKS: Record<string, Task> = {
  'pack-bag': { title: 'Pack your own bag', emoji: '🎒', plays: 1, self: true, when: 'before', who: 'all' },
  'find-on-map': { title: 'Find our first stop on a map', emoji: '🗺️', plays: 1, self: true, when: 'before', who: 'all' },
  'say-thank-you': { title: 'Say thank you in the local language', emoji: '🙏', plays: 2, self: false, when: 'any', who: 'all' },
  'try-new-food': { title: 'Try a food you have never had', emoji: '🍡', plays: 2, self: false, when: 'during', who: 'all' },
}

export const TaskSet = z.object({ taskId: id, fields: Task.partial() })
export const TaskRemoved = z.object({ taskId: id })
/** A child asks a grown-up to confirm a task (it waits in Needs you). */
export const TaskAsked = z.object({ taskId: id, for: z.string().min(1), day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/) })
/** A task is done: self-ticked by the child, or confirmed by a grown-up. It pays once per child. */
export const TaskDone = z.object({ taskId: id, for: z.string().min(1), day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/) })

export const REWARD_EVENTS = {
  'reward.paid': RewardPaid,
  'task.set': TaskSet,
  'task.removed': TaskRemoved,
  'task.asked': TaskAsked,
  'task.done': TaskDone,
  'prize.set': PrizeSet,
  'prize.removed': PrizeRemoved,
  'prize.won': PrizeWon,
  'prize.redeemed': PrizeRedeemed,
  'reward.awarded': RewardAwarded,
  'reward.earned': RewardEarned,
  'rewards.rules.set': RewardRulesSet,
} as const
